import CryptoJS from "crypto-js";
import { EPermission } from "@/constants/permission.enum";
import { IPathPermission } from "@/types/base-type";

export const PERMISSIONS_COOKIE_NAME = "permissions";

const ENTRY_SEPARATOR = "!";
const VALUE_SEPARATOR = "~";
const SIGNED_COOKIE_PREFIX = "v1";
const SIGNATURE_SEPARATOR = ".";
const PERMISSIONS_COOKIE_MAX_AGE = 60 * 60 * 24 * 30;

type ParsedPermissionsCookie = {
  permissions: IPathPermission[];
  shouldRefresh: boolean;
};

function getPermissionsCookieSecret() {
  return process.env.PERMISSIONS_COOKIE_SECRET || process.env.NEXTAUTH_SECRET;
}

function canVerifyPermissionsCookieSignature() {
  return Boolean(getPermissionsCookieSecret());
}

function signPermissionsPayload(payload: string) {
  const secret = getPermissionsCookieSecret();

  if (!secret) {
    throw new Error("Missing permissions cookie signing secret");
  }

  return CryptoJS.HmacSHA256(payload, secret).toString(CryptoJS.enc.Hex);
}

function encodeSignedPermissions(payload: string) {
  const signature = signPermissionsPayload(payload);
  return `${SIGNED_COOKIE_PREFIX}${SIGNATURE_SEPARATOR}${signature}${SIGNATURE_SEPARATOR}${payload}`;
}

function parseSignedPermissions(value: string) {
  const match = value.match(/^v1\.([a-f0-9]{64})\.(.*)$/);

  if (!match) {
    return null;
  }

  return {
    signature: match[1],
    payload: match[2],
  };
}

function parseUnsignedCompactPermissions(
  value: string
): IPathPermission[] | null {
  const compactPermissions = parseCompactPermissions(value);
  return compactPermissions?.length ? compactPermissions : null;
}

function toPermissionMask(permissions: EPermission[]) {
  return permissions.reduce(
    (mask, permission) => mask | (1 << (Number(permission) - 1)),
    0
  );
}

function fromPermissionMask(mask: number): EPermission[] {
  return Object.values(EPermission).filter(
    (permission): permission is EPermission =>
      typeof permission === "number" && (mask & (1 << (permission - 1))) !== 0
  );
}

function parseCompactPermissions(value: string): IPathPermission[] | null {
  if (!value.includes(VALUE_SEPARATOR)) {
    return null;
  }

  const permissions = value
    .split(ENTRY_SEPARATOR)
    .filter(Boolean)
    .map((entry) => {
      const separatorIndex = entry.lastIndexOf(VALUE_SEPARATOR);

      if (separatorIndex <= 0) {
        throw new Error("Invalid permissions entry");
      }

      const path = entry.slice(0, separatorIndex);
      const mask = Number.parseInt(entry.slice(separatorIndex + 1), 36);

      if (!path.startsWith("/") || Number.isNaN(mask)) {
        throw new Error("Invalid permissions entry");
      }

      return {
        path,
        permissions: fromPermissionMask(mask),
      };
    });

  return permissions;
}

export function serializePermissionsCookie(permissions: IPathPermission[]) {
  const payload = permissions
    .map(({ path, permissions: grantedPermissions }) => {
      const mask = toPermissionMask(grantedPermissions);
      return `${path}${VALUE_SEPARATOR}${mask.toString(36)}`;
    })
    .join(ENTRY_SEPARATOR);

  return encodeSignedPermissions(payload);
}

export function getPermissionsCookieOptions() {
  return {
    path: "/",
    httpOnly: false,
    secure: process.env.NODE_ENV === "production",
    sameSite: "lax" as const,
    maxAge: PERMISSIONS_COOKIE_MAX_AGE,
  };
}

export function readPermissionsCookie(
  value?: string | null
): ParsedPermissionsCookie {
  if (!value) {
    return { permissions: [], shouldRefresh: false };
  }

  const signedPermissions = parseSignedPermissions(value);

  if (signedPermissions) {
    try {
      if (!canVerifyPermissionsCookieSignature()) {
        const permissions =
          parseCompactPermissions(signedPermissions.payload) ?? [];
        return { permissions, shouldRefresh: false };
      }

      const expectedSignature = signPermissionsPayload(
        signedPermissions.payload
      );

      if (expectedSignature !== signedPermissions.signature) {
        return { permissions: [], shouldRefresh: false };
      }

      const permissions =
        parseCompactPermissions(signedPermissions.payload) ?? [];

      return { permissions, shouldRefresh: false };
    } catch {
      return { permissions: [], shouldRefresh: false };
    }
  }

  const compactPermissions = parseUnsignedCompactPermissions(value);

  if (compactPermissions) {
    return { permissions: compactPermissions, shouldRefresh: true };
  }

  return { permissions: [], shouldRefresh: false };
}

export function deserializePermissionsCookie(
  value?: string | null
): IPathPermission[] {
  return readPermissionsCookie(value).permissions;
}
